import React, { useState, useEffect, useRef } from 'react';
import Header from './Header'; // adjust path
import Footer from './Footer'; // adjust path

import GetInTouch from "../../assets/img/ContactUs.png"

// --- HELPER & UTILITY COMPONENTS (brought in from components.jsx) ---

const useOnScreen = (options) => {
  const ref = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current; // Capture ref's current value
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      }
    }, options);

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, options]);

  return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
  const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
  return (
    <section
      ref={ref}
      id={id}
      className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
    >
      {children}
    </section>
  );
};

const PageHeader = ({ imageSrc }) => (
  <section className="relative py-10 bg-slate-50">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
      <img
        src={imageSrc}
        alt="Get In Touch"
        className="mx-auto w-full max-w-md object-contain"
      />
    </div>
  </section>
);

const CheckCircle = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// --- ICONS FOR THIS PAGE ---
const Phone = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const MapPin = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const Mail = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
  </svg>
);

const ContactUsPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <Header />

      <main>
        <PageHeader
          imageSrc={GetInTouch} />

        <AnimatedSection className="py-16" id="contact-details">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Information */}
              <div className="space-y-6 sm:space-y-8">
                <div>
                  <h2 className="text-2xl  sm:text-3xl font-extrabold bg-gradient-to-r from-gray-800 to-indigo-700 bg-clip-text text-transparent">
                    Contact Information
                  </h2>
                  <p className="mt-2 text-base sm:text-lg text-slate-600">
                    Find us at our office or drop us a line through email or
                    phone.
                  </p>
                </div>


                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5  text-violet-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">
                        Our Office
                      </h3>
                      {/* <p className="text-slate-600">Holborn Gate, 330 High Holborn,</p>
                      <p className="text-slate-600">
                        London, WC1V 7QT, United Kingdom
                      </p> */}
                      <p className="text-slate-600">
                           B-14A, IInd Floor, Institutional Area, Block B, Sector 62, Noida, Uttar Pradesh 201309, India
                      </p>

                  
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5  text-violet-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">
                        Email Us
                      </h3>
                      <a
                        href="mailto:support@hlfppt.org"
                        className="text-slate-600 hover:text-violet-600"
                      >
                        support@hlfppt.org
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">
                        Call Us
                      </h3>
                      {/* <a
                        href="tel:+447500634047"
                        className="text-slate-600 hover:text-violet-600"
                      >
                        +44 7500 634047
                      </a> */}
                    </div>
                  </div>
                </div>
                {/* map */}
                <div className="mt-8 w-full flex items-center justify-center">
                  <iframe
                  src="https://www.google.com/maps?q=Hindustan%20Latex%20Family%20Planning%20Promotion%20Trust%20Noida%20Uttar%20Pradesh%20India&output=embed"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="rounded-xl shadow-md"
                  ></iframe>
                </div>

              </div>

              {/* Contact Form */}
              <div className="bg-white  p-6 sm:p-8 rounded-2xl shadow-2xl border w-full">
                {submitted ? (
                  <div className="text-center py-10 flex flex-col items-center justify-center h-full">
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                      Thank You!
                    </h2>
                    <p className="mt-1 text-slate-600">
                      Your message has been sent. We'll get back to you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-indigo-700 bg-clip-text text-transparent mb-6">
                      Send us a Message
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-slate-700"
                        >
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          required
                          className="mt-1 block w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-slate-700"
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          className="mt-1 block w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="subject"
                          className="block text-sm font-medium text-slate-700"
                        >
                          Subject
                        </label>
                        <input
                          type="text"
                          id="subject"
                          required
                          className="mt-1 block w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="message"
                          className="block text-sm font-medium text-slate-700"
                        >
                          Message
                        </label>
                        <textarea
                          id="message"
                          rows="4"
                          required
                          className="mt-1 block w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        ></textarea>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <button
                        type="submit"
                        className="bg-gradient-to-r from-indigo-500 via-violet-600 to-fuchsia-600 text-white font-semibold py-3 px-8 rounded-lg shadow-md hover:bg-blue-700 transition-all duration-300 transform hover:scale-105"
                      >
                        Send Message
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

          </div>
        </AnimatedSection>
      </main>

      <Footer />
    </>
  );
};

export default ContactUsPage;
